import {
  currentPartnerSlug,
  readReference,
  writeReference,
} from './referenceStore';
import {
  REFERENCE_PARAM_PATTERN,
  REFERENCE_PATTERN,
} from '@/config/reference';

/** Collapses case and separators so one pattern covers ReferenceID, reference_id, Reference-Id, … */
function normaliseParamKey(key: string) {
  return key.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * First accepted parameter carrying a non-empty value, in the order they appear in the URL.
 *
 * Skipping empties rather than taking the first match outright means `?ref_id=&reference_id=ABC`
 * still finds ABC, which is the shape a half-populated provider template produces.
 */
function readReferenceParam(url: string) {
  for (const [key, value] of new URL(url).searchParams) {
    if (!REFERENCE_PARAM_PATTERN.test(normaliseParamKey(key))) continue;

    const trimmed = value.trim();
    if (trimmed) return trimmed;
  }
}

/**
 * Captures a provider reference off the landing URL.
 *
 * Write-once. The param only appears on the landing navigation and does not propagate — every
 * later navigation rebuilds its own query string (?tid=, ?orderId=) — but rootLoader re-runs on
 * every navigation and every revalidation, so this can be called again while the user is still
 * on the landing URL.
 */
export function captureReferenceFromUrl(url: string) {
  const incoming = readReferenceParam(url);

  if (!incoming) return;

  if (!REFERENCE_PATTERN.test(incoming)) {
    reportError(new Error('Discarded malformed reference id'));
    return;
  }

  const existing = readReference();

  // Same reference already captured: leave it alone. Refreshing capturedAt would push the TTL
  // out indefinitely.
  if (existing?.referenceId === incoming) return;

  // Precedence is last-write-wins: a fresh deliberate click is stronger evidence than a stored
  // value, and the TTL already bounds staleness. For first-write-wins instead, add:
  //   if (existing) return;

  const stored = writeReference({
    referenceId: incoming,
    capturedAt: Date.now(),
    partner: currentPartnerSlug(),
  });

  if (!stored) {
    reportError(
      new Error('Reference id could not be stored; attribution unavailable this visit')
    );
  }
}
